import {
  Item,
  ItemVendorNum,
  NuorderBrand,
  NuorderBrandAddress,
  NuorderOrder,
  NuorderProduct,
  NuorderProductOrderable,
  Purchase,
  PurchaseLine,
  Vendor,
  VendorPurchasingCurrency,
  VendorShop,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import {
  parserFactory,
  yupArray,
  yupBool,
  yupDate,
  yupEnum,
  yupFloat,
  yupInt,
  yupObject,
  yupString,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/parserUtils';

export const parsePurchaseResponse = parserFactory<Purchase>({
  orderID: yupInt,
  archived: yupBool,
  arrivalDate: yupDate,
  b2bOrderNumber: yupString,
  b2bOrderUID: yupString,
  complete: yupBool,
  costsModifiedAfterShipment: yupBool,
  createdByEmployeeID: yupInt,
  createTime: yupDate,
  discount: yupFloat,
  discountIsPercent: yupBool,
  discountMethod: yupEnum<Purchase['discountMethod']>(['evenly', 'proportionally', 'total']),
  discountMoneyValue: yupFloat,
  discountMoneyVendorValue: yupFloat,
  discountPercentValue: yupFloat,
  hasShipments: yupBool,
  noteID: yupInt,
  orderedDate: yupDate,
  otherCost: yupFloat,
  otherVendorCost: yupFloat,
  receivedDate: yupDate,
  refNum: yupString,
  shipCost: yupFloat,
  shipInstructions: yupString,
  shipVendorCost: yupFloat,
  shippingCostMethod: yupEnum<Purchase['shippingCostMethod']>([
    'evenly',
    'proportionally',
    'total',
  ]),
  shopID: yupInt,
  stockInstructions: yupString,
  subTotalCost: yupFloat,
  timeStamp: yupDate,
  totalCost: yupFloat,
  totalDiscount: yupFloat,
  totalQuantity: yupInt,
  vendorCurrencyCode: yupString,
  vendorCurrencyRate: yupFloat,
  vendorID: yupInt,
  Note: yupObject({
    noteID: yupInt,
    note: yupString,
    isPublic: yupBool,
    timeStamp: yupDate,
  }),
});

export const parsePurchaseLineResponse = parserFactory<PurchaseLine>({
  orderLineID: yupInt,
  checkedIn: yupInt,
  createTime: yupDate,
  discountMoneyValue: yupFloat,
  discountMoneyVendorValue: yupFloat,
  discountPercentValue: yupFloat,
  itemID: yupInt,
  numReceived: yupInt,
  orderID: yupInt,
  originalPrice: yupFloat,
  price: yupFloat,
  quantity: yupInt,
  shippingCost: yupFloat,
  shippingVendorCost: yupFloat,
  timeStamp: yupDate,
  total: yupFloat,
  vendorCost: yupFloat,
});

export const parseItemResponse = parserFactory<Item>({
  itemID: yupInt,
  archived: yupBool,
  avgCost: yupFloat,
  categoryID: yupInt,
  createTime: yupDate,
  customSku: yupString,
  defaultCost: yupFloat,
  defaultVendorID: yupInt,
  departmentID: yupInt,
  description: yupString,
  discountable: yupBool,
  ean: yupString,
  itemMatrixID: yupInt,
  itemType: yupEnum<Item['itemType']>([
    'default',
    'non_inventory',
    'serialized',
    'box',
    'serialized_assembly',
    'assembly',
  ]),
  manufacturerID: yupInt,
  manufacturerSku: yupString,
  modelYear: yupInt,
  publishToEcom: yupBool,
  seasonID: yupInt,
  serialized: yupBool,
  systemSku: yupInt,
  tax: yupBool,
  taxClassID: yupInt,
  timeStamp: yupDate,
  upc: yupString,
  ItemVendorNums: yupArray(
    yupObject<ItemVendorNum>({
      itemVendorNumID: yupInt,
      b2bCatalogUUID: yupString,
      cost: yupFloat,
      itemID: yupInt,
      timeStamp: yupDate,
      value: yupString,
      vendorID: yupInt,
    })
  ),
});

export const parseItemVendorNumResponse = parserFactory<ItemVendorNum>({
  itemVendorNumID: yupInt,
  b2bCatalogUUID: yupString,
  cost: yupFloat,
  itemID: yupInt,
  timeStamp: yupDate,
  value: yupString,
  vendorID: yupInt,
});

export const parseVendorResponse = parserFactory<Vendor>({
  vendorID: yupInt,
  accountNumber: yupString,
  archived: yupBool,
  b2bSellerUID: yupString,
  name: yupString,
  priceLevel: yupString,
  shareSellThrough: yupBool,
  timeStamp: yupDate,
  updateCost: yupBool,
  updateDescription: yupBool,
  updatePrice: yupBool,
  purchasingCurrency: yupObject<VendorPurchasingCurrency>({
    code: yupString,
    symbol: yupString,
    rate: yupFloat,
  }),
});

export const parseVendorShopResponse = parserFactory<VendorShop>({
  vendorShopID: yupInt,
  b2bShippingAddressUID: yupString,
  createTime: yupDate,
  shopID: yupInt,
  timeStamp: yupDate,
  vendorID: yupInt,
});

const nuorderProductSchema = {
  attributes: yupArray(
    yupObject({
      key: yupString,
      value: yupString,
      displayName: yupString,
    })
  ),
  brand_id: yupString,
  brand_name: yupString,
  category: yupString,
  color: yupString,
  custom_attributes: yupObject<Record<string, string>>({}),
  global_group_id: yupString,
  group_id: yupString,
  id: yupString,
  images: yupArray(
    yupObject({
      id: yupString,
      image_url: yupString,
    })
  ),
  name: yupString,
  season: yupString,
  seller_id: yupString,
  seller_name: yupString,
  size: yupString,
  size_order: yupInt,
  supplier_id: yupString,
  upc: yupString,
  variant_count: yupString,
  varies_by: yupArray(
    yupObject({
      dimension: yupArray(yupString),
    })
  ),
};

export const parseNuorderProductResponse = parserFactory<NuorderProduct>(nuorderProductSchema);

const nuorderProductOrderableSchema = {
  ...nuorderProductSchema,
  inventory: yupArray(
    yupObject({
      display_name: yupString,
      period_start: yupInt,
      prebook: yupBool,
      quantity: yupInt,
      warehouse_id: yupString,
    })
  ),
  is_nuorder: yupBool,
  orderable_source: yupBool,
  pricing: yupArray(
    yupObject({
      amount: yupString,
      currency: yupString,
      source: yupString,
      type: yupString,
    })
  ),
};

export const parseNuorderProductOrderableResponse = parserFactory<NuorderProductOrderable>(
  nuorderProductOrderableSchema
);

export const parseNuorderOrderResponse = parserFactory<NuorderOrder>({
  order_id: yupString,
  archived: yupBool,
  brand_id: yupString,
  billing_address: yupObject({
    city: yupString,
    country: yupString,
    line_1: yupString,
    line_2: yupString,
    state: yupString,
    zip: yupString,
  }),
  brand_name: yupString,
  created_on: yupInt,
  creator: yupString,
  currency_code: yupString,
  customer_po_number: yupString,
  edited: yupBool,
  line_items: yupArray(
    yupObject({
      original_price: yupFloat,
      price: yupFloat,
      product_id: yupString,
      quantity: yupInt,
      ship_end: yupInt,
      ship_start: yupInt,
      warehouse_id: yupString,
    })
  ),
  locked: yupBool,
  modified_on: yupInt,
  notes: yupString,
  order_group_id: yupString,
  order_number: yupString,
  retailer: yupObject({
    buyer: yupString,
    buyer_name: yupString,
    ref: yupString,
    retailer_name: yupString,
  }),
  ship_end: yupInt,
  ship_start: yupInt,
  shipping_address: yupObject({
    city: yupString,
    country: yupString,
    line_1: yupString,
    line_2: yupString,
    state: yupString,
    zip: yupString,
  }),
  split: yupBool,
  status: yupEnum<NuorderOrder['status']>([
    'approved',
    'archived',
    'cancelled',
    'draft',
    'edited',
    'pending',
    'processed',
    'review',
    'shipped',
    'working',
  ]),
  total: yupFloat,
  total_line_items: yupInt,
  total_line_items_quantity: yupInt,
  total_units_quantity: yupInt,
});

export const parseNuorderBrandResponse = parserFactory<NuorderBrand>({
  id: yupString,
  name: yupString,
  companies: yupArray(
    yupObject({
      id: yupString,
      name: yupString,
      warehouse_id: yupString,
      addresses: yupArray(
        yupObject<NuorderBrandAddress>({
          id: yupString,
          warehouse_id: yupString,
          display_name: yupString,
          type: yupEnum<NuorderBrandAddress['type']>(['both', 'billing', 'shipping']),
          line_1: yupString,
          line_2: yupString,
          city: yupString,
          state: yupString,
          zip: yupString,
          country: yupString,
          phone: yupString,
          default_shipping: yupBool,
          default_billing: yupBool,
          billing_code: yupString,
          shipping_code: yupString,
          external_reference: yupString.nullable(true),
        })
      ),
    })
  ),
});
